import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Flex, Radio, useColorModeValue } from "@chakra-ui/react";
import VideoMode from "src/constants/videoMode";
import {
  getVideoQuality,
  getVideoMode,
  getAutoUpdateVideoMode
} from "src/duck/selectors";
import {
  videoQualityChanged,
  videoModeChanged,
  autoUpdateVideoMode
} from "src/duck/actions";
import NavigationBar from "src/components/NavigationBar";
import { BodyText2 } from "src/atoms/typography";
import { H3, Subtitle } from "src/atoms/typography";
import { useParentStation } from "src/modules/parent-station/ParentStation/Context";
import { getVideoConstraints } from "src/modules/rtc/media-constraints";

function VideoSelect({ t, mb, checked, value, onChange, string }) {
  return (
    <Flex as="label" mb={mb} alignItems="center">
      <Radio
        value={value}
        isChecked={checked}
        onChange={(e) => onChange(e.target.value)}
      />
      <Subtitle ml="14px" color={"highEmphasis"}>
        {t(string || `videoSettings.quality.${value}`)}
      </Subtitle>
    </Flex>
  );
}

export default function VideoSettings({ onClose, onBack }) {
  const borderColor = useColorModeValue("black12", "white8");
  const { t } = useTranslation();
  const parentStation = useParentStation();
  const dispatch = useDispatch();

  const videoQuality = getVideoConstraints();
  const storedVideoQuality = useSelector(getVideoQuality);
  const storedVideoMode = useSelector(getVideoMode);
  const shouldAutoUpdateVideoMode = useSelector(getAutoUpdateVideoMode);

  function handleVideoQualityChange(videoQuality) {
    dispatch(videoQualityChanged(videoQuality));
    if (parentStation) {
      parentStation.onVideoQualityChange(videoQuality);
    }
  }

  function handleVideoModeChange(videoMode) {
    dispatch(autoUpdateVideoMode(false));
    dispatch(videoModeChanged(videoMode));
  }

  function toggleAutoUpdateVideoMode() {
    dispatch(autoUpdateVideoMode(true));
  }

  return (
    <>
      <NavigationBar
        headingKey="videoSettings.heading"
        onClose={onClose}
        onBack={onBack}
      />
      <Box p="24px">
        <Flex width="100%" gap="44px">
          <Box width="50%" borderRight="1px solid" borderColor={borderColor}>
            <H3 color="highEmphasis" mb="18px">
              {t("videoSettings.quality.title")}
            </H3>
            <VideoSelect
              onChange={handleVideoQualityChange}
              value={videoQuality.HIGH.id}
              t={t}
              mb="16px"
              checked={storedVideoQuality === videoQuality.HIGH.id}
            />
            <VideoSelect
              onChange={handleVideoQualityChange}
              value={videoQuality.MEDIUM.id}
              t={t}
              mb="16px"
              checked={storedVideoQuality === videoQuality.MEDIUM.id}
            />
            <VideoSelect
              onChange={handleVideoQualityChange}
              value={videoQuality.LOW.id}
              t={t}
              checked={storedVideoQuality === videoQuality.LOW.id}
            />
            <BodyText2 color="mediumEmphasis" mt="24px" pr="24px">
              {t("videoSettings.quality.recommendation")}
            </BodyText2>
          </Box>
          <Box width="50%">
            <H3 color={"highEmphasis"} mb="18px">
              {t("videoSettings.scaling.title")}
            </H3>
            <VideoSelect
              string="videoSettings.scaling.auto"
              onChange={toggleAutoUpdateVideoMode}
              value="auto"
              mb="16px"
              t={t}
              checked={shouldAutoUpdateVideoMode}
            />
            <VideoSelect
              string="videoSettings.scaling.fill"
              onChange={handleVideoModeChange}
              value={VideoMode.COVER}
              mb="16px"
              t={t}
              checked={
                storedVideoMode === VideoMode.COVER &&
                !shouldAutoUpdateVideoMode
              }
            />
            <VideoSelect
              string="videoSettings.scaling.fit"
              onChange={handleVideoModeChange}
              value={VideoMode.CONTAIN}
              t={t}
              checked={
                storedVideoMode === VideoMode.CONTAIN &&
                !shouldAutoUpdateVideoMode
              }
            />
          </Box>
        </Flex>
      </Box>
    </>
  );
}
